/**
 * Web push (decision 0113): the shapes of the signed-in person's push devices and preferences, which notifications
 * push, and where a notification opens. The cloud and the web share these. No I/O.
 */
import { Schema } from "effect"
import { ClockTime } from "./agent-page"
import type { Notification } from "./domain/agentlife"
import { Millis } from "./ids"

/**
 * What a person can switch on or off: cards that need them, their agents' briefs, completed work, and (onboarding
 * 2026-10-23 §4) an agent's chat reply to a message they sent.
 */
export const PushCategory = Schema.Literals(["needs_me", "briefs", "completed", "replies"])
export type PushCategory = typeof PushCategory.Type

/**
 * Onboarding 2026-10-23 §4: the `digest` source of "Guy replied to you": one row per agent and person, a newer reply
 * replaces it (unread again). Opens that agent's chat.
 */
export const REPLY_DIGEST_PREFIX = "reply:"
export const replyDigestRef = (agentId: string): string => `${REPLY_DIGEST_PREFIX}${agentId}`
/** The agent a reply notification's `digestRef` names, or null for any other digest. */
export const replyAgentOf = (digestRef: string): string | null =>
  digestRef.startsWith(REPLY_DIGEST_PREFIX) ? digestRef.slice(REPLY_DIGEST_PREFIX.length) || null : null

/** No pushes between `from` and `to` (local time in `tz`, an IANA zone); the notification still lands in the bell. */
export const QuietHours = Schema.Struct({
  from: ClockTime,
  to: ClockTime,
  tz: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64)),
})
export type QuietHours = typeof QuietHours.Type

export const PushPrefs = Schema.Struct({
  needsMe: Schema.Boolean,
  briefs: Schema.Boolean,
  completed: Schema.Boolean,
  /** Onboarding 2026-10-23 §4: an agent replied to my message in chat (on by default). */
  replies: Schema.Boolean,
  quietHours: Schema.NullOr(QuietHours),
})
export type PushPrefs = typeof PushPrefs.Type

/** `PUT /me/push/prefs`: `replies` may be absent (a page loaded before it existed); the stored value then stays. */
export const PushPrefsInput = Schema.Struct({
  needsMe: Schema.Boolean,
  briefs: Schema.Boolean,
  completed: Schema.Boolean,
  replies: Schema.optionalKey(Schema.Boolean),
  quietHours: Schema.NullOr(QuietHours),
})
export type PushPrefsInput = typeof PushPrefsInput.Type

/** Blocking and soon cards push, briefs and replies to me; completed work (fyi) only when asked for. */
export const DEFAULT_PUSH_PREFS: PushPrefs = {
  needsMe: true,
  briefs: true,
  completed: false,
  replies: true,
  quietHours: null,
}

/** One browser or installed app that receives pushes. */
export const PushDevice = Schema.Struct({
  id: Schema.String,
  /** "iPhone · Safari", from the user agent at subscribe time. */
  label: Schema.String,
  /** The push service's host (web.push.apple.com, fcm.googleapis.com …), for telling devices apart. */
  service: Schema.String,
  createdAt: Millis,
  lastSuccessAt: Schema.NullOr(Millis),
  failureCount: Schema.Int,
})
export type PushDevice = typeof PushDevice.Type

/** GET /api/me/push */
export const PushStatus = Schema.Struct({
  /** False while the Worker has no VAPID key: the page says push is not set up. */
  enabled: Schema.Boolean,
  /** The VAPID public key (base64url, uncompressed P-256 point): the browser's `applicationServerKey`. */
  publicKey: Schema.NullOr(Schema.String),
  devices: Schema.Array(PushDevice),
  prefs: PushPrefs,
})
export type PushStatus = typeof PushStatus.Type

/** PUT /api/me/push/subscription: the browser's `PushSubscription.toJSON()`, plus the endpoint it replaces. */
export const PushSubscribeBody = Schema.Struct({
  endpoint: Schema.String.check(Schema.isMaxLength(2048), Schema.isPattern(/^https:\/\/[^\s]+$/)),
  keys: Schema.Struct({
    p256dh: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{80,100}={0,2}$/)),
    auth: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{16,32}={0,2}$/)),
  }),
  /** `pushsubscriptionchange`: the old endpoint this one replaces (deleted with it). */
  oldEndpoint: Schema.optional(Schema.String.check(Schema.isMaxLength(2048))),
})
export type PushSubscribeBody = typeof PushSubscribeBody.Type

/** POST /api/me/push/test: how many devices took the test push, and how many were removed as gone. */
export const PushTestResult = Schema.Struct({ sent: Schema.Int, failed: Schema.Int, removed: Schema.Int })
export type PushTestResult = typeof PushTestResult.Type

/** What the service worker receives (the decrypted push payload). */
export interface PushPayload {
  readonly title: string
  readonly body: string
  /** Same-origin path the notification opens. */
  readonly url: string
  /** Replaces an earlier notification with the same tag (one per notification row). */
  readonly tag: string
  /** Unread notifications across workspaces, for the app icon badge where supported. */
  readonly badge?: number
  /**
   * Decision 0144: the card's recommended answer as a notification action ("Approve"), with "Open". The service worker
   * POSTs `answer.body` to `answer.url` (same origin) and opens `url` when that fails.
   */
  readonly actions?: ReadonlyArray<{ readonly action: string; readonly title: string }>
  readonly answer?: { readonly url: string; readonly body: string }
}

/** Which switch a notification falls under; null when it never pushes (ambient). */
export const pushCategoryOf = (n: Pick<Notification, "tier" | "source">): PushCategory | null => {
  if (n.tier === "ambient") return null
  if (n.tier === "blocking" || n.tier === "soon") return "needs_me"
  if (n.source._tag === "digest" && n.source.digestRef.startsWith("brief:")) return "briefs"
  if (n.source._tag === "digest" && replyAgentOf(n.source.digestRef) !== null) return "replies"
  return "completed"
}

export const pushAllowed = (prefs: PushPrefs, category: PushCategory | null): boolean =>
  category === "needs_me"
    ? prefs.needsMe
    : category === "briefs"
      ? prefs.briefs
      : category === "completed"
        ? prefs.completed
        : category === "replies"
          ? prefs.replies
          : false

/** Where a notification opens in the app (the bell and the push use the same link). */
export const notificationPath = (n: Pick<Notification, "workspaceId" | "projectId" | "source">): string => {
  const base = `/w/${encodeURIComponent(n.workspaceId)}/p/${encodeURIComponent(n.projectId ?? "")}`
  const source = n.source
  if (source._tag === "item" && source.ref) return `${base}/i/${encodeURIComponent(source.ref)}`
  if (source._tag === "proposal") return `${base}?view=needs&card=${encodeURIComponent(source.proposalId)}`
  if (source._tag === "work") return `${base}?view=overview#${encodeURIComponent(source.requestId)}`
  const replyAgent = source._tag === "digest" ? replyAgentOf(source.digestRef) : null
  if (replyAgent) return `${base}?view=chat&agent=${encodeURIComponent(replyAgent)}`
  return base
}
